import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CombinedEmployeeSearch } from '../../models/combined-employee-search.model';
import { TreeviewItem } from 'ngx-treeview';
import { TreeViewEntity } from '../../models/tree-view-entity.model';
import { BusinessUnitService } from '../../services/business-unit.service';
import { UserService } from '../../services/user.service';
import { NamedEntity } from '../../models/named-entity.model';
import { CombinedEmployeeSearchValue } from '../../models/combined-employee-search-value.model';
import { CombinedEmployeeSearchType } from '../../models/combined-employee-search-type.enum';
import { EmployeeSetting } from './employee.setting.model';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { ProcessService } from '../../services/process.service';
import { ConfigService } from '../../services/config.service';
import { EmployeeSearchRequest } from './employee-search-request.model';
import { INgxSelectOption } from 'ngx-select-ex/ngx-select/ngx-select.interfaces';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-employee-selection',
  templateUrl: './employee-selection.component.html',
  styleUrls: ['./employee-selection.component.scss'],
})
export class EmployeeSelectionComponent implements OnInit, OnDestroy {
  @Input()
  employeeSelectionSettings: Array<CombinedEmployeeSearch> = [];
  @Input()
  employeeTypes: Array<number>;
  @Input()
  isTypeFilterEnabled: boolean;
  @Input()
  showDistributionList: boolean = false;
  @Input()
  distributionListsSelcted: Array<NamedEntity> = [];

  selectedSettings: Array<EmployeeSetting> = [];
  grades: Array<NamedEntity> = [];
  positions: Array<NamedEntity> = [];
  employees: Array<NamedEntity> = [];
  subscription: Subscription;
  searchRequest: EmployeeSearchRequest;
  businessUnitTreeView: Array<TreeViewEntity>;
  isAbleToSearch: boolean = false;

  placeholdedrDistributionGroupSelect = 'Search distribution groups';
  distributionListsEntities: Array<NamedEntity> = [];
  distributionLists: Array<TreeviewItem> = [];
  selectedDistributionLists: Array<number> = [];
  selectedDistributionListsItems: Array<TreeviewItem> = [];

  searchTextChanged$ = new Subject<string>();

  constructor(
    private businessUnitService: BusinessUnitService,
    private configService: ConfigService,
    private userService: UserService,
    private processService: ProcessService
  ) {
    this.subscribeOnSave();
  }

  ngOnInit() {
    this.refreshComponent();
    this.processService.employeeTypeChanged$.subscribe(() => {
      if (!this.isTypeFilterEnabled) {
        return;
      }
      this.selectedSettings = [];
      this.refreshComponent();
    });
    this.searchTextChanged$
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((result) => this.makeSearcheRequest(result));
  }

  makeSearcheRequest(search: string) {
    this.processService.getDistributionLists(search).subscribe((result) => {
      this.distributionListsEntities = result;
      this.distributionLists = this.mapNamedArrayToNgxSelect(
        this.distributionListsEntities,
        []
      );
    });
  }

  onSearchTextTyped(search: string) {
    this.distributionLists = [];
    if (search.length > 8) {
      this.searchTextChanged$.next(search);
    }
  }

  blankSearch(search: string, item: INgxSelectOption) {
    return true;
  }

  onBusinessUnitSelected(
    item: TreeViewEntity,
    employeeSetting: EmployeeSetting
  ) {
    employeeSetting.selectedBusinessUnitId = item.id;
    employeeSetting.selectedBusinessUnitIds = this.getSelectedIds(item, []);
    this.searchRequest.businessUnitIds =
      employeeSetting.selectedBusinessUnitIds;

    this.userService
      .getPositions(this.searchRequest)
      .subscribe(
        (r) =>
          (employeeSetting.selectorPositions = this.mapNamedArrayToNgxSelect(
            r,
            employeeSetting.selectedPositionIds
          ))
      );

    this.userService
      .getGrades(this.searchRequest)
      .subscribe(
        (r) =>
          (employeeSetting.selectorGrades = this.mapNamedArrayToNgxSelect(
            r,
            employeeSetting.selectedGradeIds
          ))
      );

    this.filterEmployees(employeeSetting);
  }

  onSelectDistributionGroup(id: number) {
    let group = this.distributionLists.find((item) => item.value == id);
    if (group) {
      this.selectedDistributionListsItems.push(group);
    }
  }

  onRemoveDistributionGroup(id: number) {
    this.selectedDistributionListsItems = this.selectedDistributionListsItems.filter(
      (item) => item.value != id
    );
  }

  onGradeChanged(items: Array<TreeviewItem>, employeeSetting: EmployeeSetting) {
    employeeSetting.selectedGradeIds = items.map((i) => i.value);
    this.filterEmployees(employeeSetting);
  }

  onPositionChanged(
    items: Array<TreeviewItem>,
    employeeSetting: EmployeeSetting
  ) {
    employeeSetting.selectedPositionIds = items.map((i) => i.value);
    this.filterEmployees(employeeSetting);
  }

  onEmployeeChanged(
    items: Array<TreeviewItem>,
    employeeSetting: EmployeeSetting
  ) {
    employeeSetting.selectedEmployeeIds = items.map((i) => i.value);
  }

  addFilterSet() {
    this.selectedSettings.push(this.createEmployeeSetting());
  }

  removeFilterSet(employeeSetting) {
    const index = this.selectedSettings.indexOf(employeeSetting);
    this.selectedSettings.splice(index, 1);
  }

  saveEmployeeSettings() {
    this.addEmployeeSearchValues();
  }

  ngOnDestroy(): void {
    if (!this.subscription) {
      return;
    }
    this.subscription.unsubscribe();
  }

  private refreshComponent() {
    const businessUnitRequestResultIndex = 0;
    const gradeRequestResultIndex = 1;
    const positionRequestResultIndex = 2;

    this.distributionListsEntities = this.distributionListsSelcted;

    this.searchRequest = {
      isTypeFilterEnabled: !!this.isTypeFilterEnabled,
      employeeTypes: this.employeeTypes,
    };
    forkJoin(
      this.businessUnitService.getTreeView(),
      this.userService.getGrades(this.searchRequest),
      this.userService.getPositions(this.searchRequest)
    ).subscribe((result) => {
      this.grades = result[gradeRequestResultIndex];
      this.positions = result[positionRequestResultIndex];
      if (
        this.employeeSelectionSettings &&
        this.employeeSelectionSettings.length > 0
      ) {
        this.setSelected();
      }
      if (
        this.distributionListsEntities &&
        this.distributionListsEntities.length > 0
      ) {
        this.setSelectedDistributionLists();
      }
      this.businessUnitTreeView = result[businessUnitRequestResultIndex];
    });
  }

  private addValuesBySearchType(
    ids: Array<number>,
    values: Array<CombinedEmployeeSearchValue>,
    searchType: CombinedEmployeeSearchType
  ) {
    if (ids) {
      ids.forEach((g) =>
        values.push({ sourceId: g, combinedEmployeeSearchType: searchType })
      );
    }
  }

  private setSelected() {
    this.selectedSettings = this.employeeSelectionSettings
      .filter((item) =>
        item.values.every(
          (inneritem) =>
            inneritem.combinedEmployeeSearchType !=
            CombinedEmployeeSearchType.DistributionLists
        )
      )
      .map((ess) => {
        const ss: EmployeeSetting = {};
        ss.selectedBusinessUnitId = this.getIdsBySearchType(
          ess,
          CombinedEmployeeSearchType.BusinessUnit
        ).find((c) => true);
        ss.selectedGradeIds = this.getIdsBySearchType(
          ess,
          CombinedEmployeeSearchType.Grade
        );
        ss.selectedPositionIds = this.getIdsBySearchType(
          ess,
          CombinedEmployeeSearchType.Position
        );
        ss.selectedEmployeeIds = this.getIdsBySearchType(
          ess,
          CombinedEmployeeSearchType.Employee
        );

        if (!ss.selectedBusinessUnitId) {
          this.initDropdownFilters(ss);
        }
        return ss;
      });
  }

  private setSelectedDistributionLists() {
    let checkedItems: Array<number> = [];

    this.employeeSelectionSettings.forEach((item) => {
      item.values.forEach((innerItem) => {
        if (
          innerItem.combinedEmployeeSearchType ==
          CombinedEmployeeSearchType.DistributionLists
        ) {
          checkedItems.push(innerItem.sourceId);
        }
      });
    });

    this.selectedDistributionLists = checkedItems;
    this.distributionLists = this.mapNamedArrayToNgxSelect(
      this.distributionListsEntities,
      this.selectedDistributionLists
    );

    this.selectedDistributionListsItems = this.distributionLists;
  }

  private initDropdownFilters(ss: EmployeeSetting) {
    ss.selectorGrades = this.mapNamedArrayToNgxSelect(
      this.grades,
      ss.selectedGradeIds
    );
    ss.selectorPositions = this.mapNamedArrayToNgxSelect(
      this.positions,
      ss.selectedPositionIds
    );
    if (ss.selectedEmployeeIds && ss.selectedEmployeeIds.length !== 0) {
      this.filterEmployees(ss);
      return;
    }
    ss.selectorEmployees = [];
  }

  private addEmployeeSearchValues() {
    this.employeeSelectionSettings.length = 0;
    this.selectedSettings.forEach((ss) => {
      const values: Array<CombinedEmployeeSearchValue> = [];
      this.addValuesBySearchType(
        ss.selectedGradeIds,
        values,
        CombinedEmployeeSearchType.Grade
      );
      this.addValuesBySearchType(
        ss.selectedPositionIds,
        values,
        CombinedEmployeeSearchType.Position
      );
      this.addValuesBySearchType(
        ss.selectedEmployeeIds,
        values,
        CombinedEmployeeSearchType.Employee
      );

      if (ss.selectedBusinessUnitId) {
        this.addValuesBySearchType(
          [ss.selectedBusinessUnitId],
          values,
          CombinedEmployeeSearchType.BusinessUnit
        );
      }
      this.employeeSelectionSettings.push({ values: values });
    });
    this.addDistributionLists();
  }

  private addDistributionLists() {
    const values: Array<CombinedEmployeeSearchValue> = [];

    if (this.selectedDistributionListsItems) {
      this.selectedDistributionListsItems.forEach((g) =>
        values.push({
          sourceId: g.value,
          combinedEmployeeSearchType:
            CombinedEmployeeSearchType.DistributionLists,
          sourceName: g.text,
        })
      );
    }

    if (values.length > 0) {
      this.employeeSelectionSettings.push({ values: values });
    }
  }

  private createEmployeeSetting(): EmployeeSetting {
    return {
      selectorGrades: this.mapNamedArrayToNgxSelect(this.grades, []),
      selectorPositions: this.mapNamedArrayToNgxSelect(this.positions, []),
      selectorEmployees: this.mapNamedArrayToNgxSelect(this.employees, []),
      selectedPositionIds: [],
      selectedGradeIds: [],
      selectedBusinessUnitIds: [],
      selectedEmployeeIds: [],
    };
  }

  private filterEmployees(employeeSetting: EmployeeSetting) {
    this.searchRequest.businessUnitIds =
      employeeSetting.selectedBusinessUnitIds;
    this.searchRequest.gradeIds = employeeSetting.selectedGradeIds;
    this.searchRequest.positionIds = employeeSetting.selectedPositionIds;

    this.userService
      .getEmployees(this.searchRequest)
      .subscribe(
        (r) =>
          (employeeSetting.selectorEmployees = this.mapNamedArrayToNgxSelect(
            r,
            employeeSetting.selectedEmployeeIds
          ))
      );
  }

  private getIdsBySearchType(
    searchSetting: CombinedEmployeeSearch,
    searchType: CombinedEmployeeSearchType
  ) {
    return searchSetting.values
      .filter((v) => v.combinedEmployeeSearchType === searchType)
      .map((c) => c.sourceId);
  }

  private getSelectedIds(entity: TreeViewEntity, ids: Array<number>) {
    if (!entity.id) {
      return;
    }
    ids.push(entity.id);
    if (entity.children) {
      entity.children.forEach((c) => this.getSelectedIds(c, ids));
    }
    return ids;
  }

  private mapNamedArrayToNgxSelect(
    array: Array<NamedEntity>,
    selectedIds: Array<number>
  ) {
    return array.map((x) =>
      Object.assign(
        new TreeviewItem({
          value: x.id,
          text: x.name,
          checked: selectedIds.indexOf(x.id) !== -1,
          children: [],
          collapsed: true,
        })
      )
    );
  }

  private subscribeOnSave() {
    if (this.subscription) {
      return;
    }
    this.subscription = this.processService.saveEmployeeSettings$.subscribe(
      () => this.saveEmployeeSettings()
    );
  }
}
